import type { DashboardLayout } from '@tempo-io/roadmap-dto';

export type Cell = { position: number };
export type Stripe = { cells: Cell[] };
export type DashboardLayoutDescriptor = {
  orientation: 'row' | 'column';
  stripes: Stripe[];
};

const rows = (...rowDefs: number[][]): DashboardLayoutDescriptor => {
  let nextPos = 0;
  return {
    orientation: 'row',
    stripes: rowDefs.map((cellsPerStripe) => ({
      cells: cellsPerStripe.map(() => ({ position: nextPos++ })),
    })),
  };
};

const rowsByCount = (cellCounts: number[]): DashboardLayoutDescriptor => {
  let nextPos = 0;
  return {
    orientation: 'row',
    stripes: cellCounts.map((count) => ({
      cells: Array.from({ length: count }, () => ({ position: nextPos++ })),
    })),
  };
};

const columnsByCount = (cellCounts: number[]): DashboardLayoutDescriptor => {
  let nextPos = 0;
  return {
    orientation: 'column',
    stripes: cellCounts.map((count) => ({
      cells: Array.from({ length: count }, () => ({ position: nextPos++ })),
    })),
  };
};

export const DASHBOARD_LAYOUT_DESCRIPTORS: Record<DashboardLayout, DashboardLayoutDescriptor> = {
  single: rowsByCount([1]),
  'two-column': rowsByCount([2]),
  'two-row': rowsByCount([1, 1]),
  'two-by-two': rowsByCount([2, 2]),

  'wide-left': columnsByCount([1, 2]),
  'wide-right': columnsByCount([2, 1]),

  'wide-top': rowsByCount([1, 2]),
  'wide-bottom': rowsByCount([2, 1]),

  'three-row': rowsByCount([1, 1, 1]),
  'three-column': rowsByCount([3]),
  'three-by-two': rowsByCount([3, 3]),
  'two-by-three': rowsByCount([2, 2, 2]),
  'three-by-three': rowsByCount([3, 3, 3]),

  'two-by-two-with-footer': rowsByCount([2, 2, 1]),
  'two-by-two-with-header': rowsByCount([1, 2, 2]),
};

export { rows };

export const layoutSlotCount = (descriptor: DashboardLayoutDescriptor): number =>
  descriptor.stripes.reduce((sum, s) => sum + s.cells.length, 0);

export const locateCell = (
  descriptor: DashboardLayoutDescriptor,
  position: number,
): { stripeIdx: number; cellIdx: number } | null => {
  for (let stripeIdx = 0; stripeIdx < descriptor.stripes.length; stripeIdx++) {
    const stripe = descriptor.stripes[stripeIdx];
    if (!stripe) continue;
    const cellIdx = stripe.cells.findIndex((c) => c.position === position);
    if (cellIdx !== -1) return { stripeIdx, cellIdx };
  }
  return null;
};
